import { WebsiteConfig } from "./WebsiteConfig";

// ── Structured theme types ──────────────────────────────────────────

export type BodyTheme = {
  fontColor?: string;
  fontSize?: string;
  layoutOption?: string;
};

export type HeadingTheme = {
  fontFamily?: string;
  fontSize?: string;
  fontColor?: string;
};

export type HeadingsTheme = {
  h1: HeadingTheme;
  h2: HeadingTheme;
};

export type ButtonStateTheme = {
  bgColor?: string;
  bgHoverColor?: string;
  borderColor?: string;
  borderHoverColor?: string;
  fontColor?: string;
  fontHoverColor?: string;
  fontSize?: string;
  fontHoverSize?: string;
  borderRadius?: string;
  hoverBorderRadius?: string;
};

export type ButtonsTheme = {
  primary: ButtonStateTheme;
  secondary: ButtonStateTheme;
};

export type HyperlinkTheme = {
  fontColor?: string;
  fontHoverColor?: string;
  underline?: boolean;
};

export type FormInputTheme = {
  labelFontColor?: string;
  inputFontColor?: string;
  backgroundColor?: string;
  border?: boolean;
  borderColor?: string;
  borderRadius?: string;
  borderStyle?: string;
  borderWidth?: string;
  borderSides?: string[];
  fontFamily?: string;
  fontSize?: string;
  errorColor?: string;
};

// ── Structured theme → WebsiteConfig field mappings (for diffing) ────────

export const BODY_THEME_KEYS: Record<keyof BodyTheme, keyof WebsiteConfig> = {
  fontColor: "bodyFontColor",
  fontSize: "bodyFontSize",
  layoutOption: "bodyLayoutOption",
};

export const H1_THEME_KEYS: Record<keyof HeadingTheme, keyof WebsiteConfig> = {
  fontFamily: "h1FontFamily",
  fontSize: "h1FontSize",
  fontColor: "h1FontColor",
};

export const H2_THEME_KEYS: Record<keyof HeadingTheme, keyof WebsiteConfig> = {
  fontFamily: "h2FontFamily",
  fontSize: "h2FontSize",
  fontColor: "h2FontColor",
};

export const PRIMARY_BTN_KEYS: Record<keyof ButtonStateTheme, keyof WebsiteConfig> = {
  bgColor: "primaryBtnBgColor",
  bgHoverColor: "primaryBtnBgHoverColor",
  borderColor: "primaryBtnBorderColor",
  borderHoverColor: "primaryBtnBorderHoverColor",
  fontColor: "primaryBtnFontColor",
  fontHoverColor: "primaryBtnFontHoverColor",
  fontSize: "primaryBtnFontSize",
  fontHoverSize: "primaryBtnFontHoverSize",
  borderRadius: "primaryBtnBorderRadius",
  hoverBorderRadius: "primaryBtnHoverBorderRadius",
};

export const SECONDARY_BTN_KEYS: Record<keyof ButtonStateTheme, keyof WebsiteConfig> = {
  bgColor: "secondaryBtnBgColor",
  bgHoverColor: "secondaryBtnBgHoverColor",
  borderColor: "secondaryBtnBorderColor",
  borderHoverColor: "secondaryBtnBorderHoverColor",
  fontColor: "secondaryBtnFontColor",
  fontHoverColor: "secondaryBtnFontHoverColor",
  fontSize: "secondaryBtnFontSize",
  fontHoverSize: "secondaryBtnFontHoverSize",
  borderRadius: "secondaryBtnBorderRadius",
  hoverBorderRadius: "secondaryBtnHoverBorderRadius",
};

export const HYPERLINK_THEME_KEYS: Record<keyof HyperlinkTheme, keyof WebsiteConfig> = {
  fontColor: "hyperlinkFontColor",
  fontHoverColor: "hyperlinkFontHoverColor",
  underline: "hyperlinkUnderline",
};

export const FORM_INPUT_THEME_KEYS: Record<keyof FormInputTheme, keyof WebsiteConfig> = {
  labelFontColor: "formInputLabelFontColor",
  inputFontColor: "formInputInputFontColor",
  backgroundColor: "formInputBackgroundColor",
  border: "formInputBorder",
  borderColor: "formInputBorderColor",
  borderRadius: "formInputBorderRadius",
  borderStyle: "formInputBorderStyle",
  borderWidth: "formInputBorderWidth",
  borderSides: "formInputBorderSides",
  fontFamily: "formInputFontFamily",
  fontSize: "formInputFontSize",
  errorColor: "formInputErrorColor",
};

/**
 * Diffs two flat objects using the provided key mapping, returning changed WebsiteConfig fields.
 */
export function diffThemeGroup<T extends Record<string, unknown>>(
  current: T,
  initial: T,
  keyMap: Record<keyof T, keyof WebsiteConfig>,
): Partial<WebsiteConfig> {
  const result: Partial<WebsiteConfig> = {};
  for (const key of Object.keys(keyMap) as (keyof T)[]) {
    const a = current[key];
    const b = initial[key];
    // Value-compare arrays (e.g. formInputTheme.borderSides) — identity compare
    // would report an unchanged same-content array as changed, or miss an
    // in-place mutation. All other theme fields are scalars.
    const changed =
      Array.isArray(a) || Array.isArray(b) ? JSON.stringify(a) !== JSON.stringify(b) : a !== b;
    if (changed) {
      const metaKey = keyMap[key] as string;
      (result as Record<string, unknown>)[metaKey] = current[key];
    }
  }
  return result;
}
